import { forwardRef, useEffect, useImperativeHandle } from 'react';

import { StudentAccessProduct, StudentApi, StudentContentAccessDetail } from 'apis/StudentApi';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixDatePicker } from 'components/GMetrixDatePicker/GMetrixDatePicker';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { Modal } from 'components/modal_manager';
import { useSuperState } from 'hooks';

export type EditStudentAccessDialogHandle = {
    hide: () => void;
    show: (studentAccessCodeId: number) => void;
    visible: boolean;
};

type EditStudentAccessDialogProps = {
    onSaved: () => void;
};

export const EditStudentAccessDialog = forwardRef<EditStudentAccessDialogHandle, EditStudentAccessDialogProps>((props, forwardedRef) => {

    const { onSaved } = props;

    const { formatMessage } = useIntl();
    const { catchWithToast, onErrorToast, onSuccessToast } = useNotification();

    const [state, setState, resetState] = useSuperState({
        active: false,
        booksRemaining: '',
        cloudAccessEnabled: false,
        cloudAccessRemaining: '',
        coursesRemaining: '',
        detail: null as StudentContentAccessDetail | null,
        expirationDate: '',
        loading: false,
        productId: null as number | null,
        saving: false,
        studentAccessCodeId: null as number | null,
        testsRemaining: '',
        visible: false
    });

    const show = (studentAccessCodeId: number) => setState({ studentAccessCodeId, visible: true });

    const hide = () => resetState();

    const numberToInput = (value: number | null): string => (value !== null && value !== undefined ? String(value) : '');

    const inputToNumber = (value: string): number | null => {

        const trimmed = value.trim();
        if (trimmed === '') {

            return null;

        }
        const parsed = Number(trimmed);
        return Number.isNaN(parsed) ? null : parsed;

    };

    const loadDetail = async () => {

        if (state.studentAccessCodeId) {

            try {

                setState({ loading: true });
                const detail = await StudentApi.GetStudentAccessDetail(state.studentAccessCodeId);
                setState({
                    active: detail.IsActive,
                    booksRemaining: numberToInput(detail.BooksRemaining),
                    cloudAccessEnabled: detail.CloudAccessRemaining != null && detail.CloudAccessRemaining > 0,
                    cloudAccessRemaining: numberToInput(detail.CloudAccessRemaining),
                    coursesRemaining: numberToInput(detail.CoursesRemaining),
                    detail,
                    expirationDate: detail.ExpirationDate,
                    loading: false,
                    productId: detail.ProductID,
                    testsRemaining: numberToInput(detail.TestsRemaining)
                });

            } catch (e) {

                catchWithToast(e, formatMessage({ id: 'failed_to_load_access_code_details.ed1f3281', defaultMessage: 'Failed to load access code details.' }));
                setState({ loading: false });

            }

        }

    };

    const handleSave = async () => {

        if (state.studentAccessCodeId) {

            try {

                setState({ saving: true });
                const result = await StudentApi.UpdateStudentAccessDetail(
                    state.studentAccessCodeId,
                    state.active,
                    state.expirationDate,
                    state.detail?.TestsRemaining !== null ? inputToNumber(state.testsRemaining) : null,
                    state.detail?.BooksRemaining !== null ? inputToNumber(state.booksRemaining) : null,
                    state.detail?.CoursesRemaining !== null ? inputToNumber(state.coursesRemaining) : null,
                    state.cloudAccessEnabled
                        ? (inputToNumber(state.cloudAccessRemaining) ?? 10)
                        : (state.detail?.CloudAccessRemaining != null ? 0 : null),
                    state.detail?.IsAssociateProduct === 1
                        && state.detail?.ProductID != null
                        && state.detail?.ProductID !== 0
                        && state.detail?.Products?.some(product => product.Id === state.productId)
                        ? state.productId
                        : null
                );

                setState({ saving: false });

                if (result.Done) {

                    onSuccessToast(result.Message || formatMessage({ id: 'successfully_updated.c1c0d6f2', defaultMessage: 'Successfully updated' }));
                    onSaved();
                    hide();

                } else {

                    onErrorToast(result.Message || formatMessage({ id: 'failed_to_update_access_code_details.e65185ca', defaultMessage: 'Failed to update access code details.' }));

                }

            } catch (e) {

                catchWithToast(e, formatMessage({ id: 'failed_to_update_access_code_details.e65185ca', defaultMessage: 'Failed to update access code details.' }));
                setState({ saving: false });

            }

        }

    };

    useEffect(() => {

        if (state.visible && state.studentAccessCodeId) {

            loadDetail();

        }

    }, [state.visible, state.studentAccessCodeId]);

    useImperativeHandle(forwardedRef, () => ({
        hide,
        show,
        visible: state.visible
    } as EditStudentAccessDialogHandle));

    const formatDisplayDate = (value: string | undefined): string => (value && !Number.isNaN(Date.parse(value)) ? new Date(value).toLocaleDateString() : '');

    const detail = state.detail;

    const cloudAccessValue = inputToNumber(state.cloudAccessRemaining);
    const cloudAccessInvalid = state.cloudAccessEnabled
        && (cloudAccessValue === null || !Number.isInteger(cloudAccessValue) || cloudAccessValue < 0 || cloudAccessValue > 32767);
    const saveDisabled = state.loading || state.saving || cloudAccessInvalid;

    return (
        <Modal
            onHide={hide}
            show={state.visible}
            size="lg"
        >
            <Modal.Header closeButton>
                <Modal.Title className="w-100 fw-bold">
                    {detail?.DisplayName ?? ''}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="bg-light">
                {state.loading || !detail
                    ? (
                        <div className="text-center p-5">
                            <GMetrixLoader/>
                        </div>
                    )
                    : (
                        <div className="d-flex flex-column gap-3">
                            <div className="card">
                                <div className="card-body">
                                    <div className="row g-3">
                                        {[
                                            { label: formatMessage({ id: 'testing_center.fda9aa47', defaultMessage: 'Testing Center' }), value: detail.TestingCenter || '-' },
                                            { label: formatMessage({ id: 'created_by.1ae66f0e', defaultMessage: 'Created By', description: 'Gridview header' }), value: detail.CreatedBy || '-' },
                                            { label: formatMessage({ id: 'date_redeemed.63ec9acb', defaultMessage: 'Date Redeemed' }), value: formatDisplayDate(detail.DateRedeemed) || '-' },
                                            { label: formatMessage({ id: 'last_modified.e4b9e26e', defaultMessage: 'Last Modified' }), value: formatDisplayDate(detail.LastModified) || '-' },
                                            { label: formatMessage({ id: 'saved_tests.78aa4f03', defaultMessage: 'Saved Tests' }), value: detail.NumSavedTests ?? 0 },
                                            { label: formatMessage({ id: 'finished_tests.0b61a38f', defaultMessage: 'Finished Tests' }), value: detail.NumFinishedTests ?? 0 }
                                        ].map(item => (
                                            <div className="col-6 col-md-4" key={item.label}>
                                                <div className="small text-muted fw-bold">
                                                    {item.label}
                                                </div>
                                                <div className="text-break">
                                                    {item.value}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="card">
                                <div className="card-body d-flex flex-column gap-3">
                                    <h6 className="text-uppercase text-muted small fw-bold m-0">
                                        <FormattedMessage defaultMessage="Settings" id="settings.277e0df0" />
                                    </h6>

                                    <div className="d-flex flex-wrap gap-4">
                                        <GMetrixCheckInput
                                            checked={state.active}
                                            label={formatMessage({ id: 'active.b9616c88', defaultMessage: 'Active' })}
                                            onChange={e => setState({ active: e.target.checked })}
                                            type="checkbox"
                                        />

                                        <GMetrixCheckInput
                                            onChange={e => setState({
                                                cloudAccessEnabled: e.target.checked,
                                                cloudAccessRemaining: e.target.checked && (state.cloudAccessRemaining.trim() === '' || Number(state.cloudAccessRemaining) <= 0) ? '10' : state.cloudAccessRemaining
                                            })}
                                            checked={state.cloudAccessEnabled}
                                            label={formatMessage({ id: 'cloud_access.95fe7c27', defaultMessage: 'Cloud Access' })}
                                            type="checkbox"
                                        />
                                    </div>

                                    <div className="row">
                                        <div className="col-12 col-md-6">
                                            <GMetrixDatePicker
                                                label={formatMessage({ id: 'expiration_date.7e4bebd2', defaultMessage: 'Expiration Date' })}
                                                onChange={(date: any) => setState({ expirationDate: date ? new Date(date).toISOString() : '' })}
                                                value={state.expirationDate}
                                            />
                                        </div>
                                    </div>

                                    {detail.IsAssociateProduct === 1 && detail.ProductID != null && detail.ProductID !== 0 && (
                                    <div className="row">
                                        <div className="col-12 col-md-6">
                                            <GMetrixDropdown<StudentAccessProduct>
                                                dataItemKey="Id"
                                                dataItems={detail.Products ?? []}
                                                disabled={(detail.Products?.length ?? 0) === 0}
                                                label={formatMessage({ id: 'associated_product_first_use.91a4c783', defaultMessage: 'Associated Product (First Use)' })}
                                                onChange={value => setState({ productId: (value as StudentAccessProduct)?.Id ?? null })}
                                                placeholder={formatMessage({ id: 'select_a_product.421ccca4', defaultMessage: 'Select a Product', description: 'Formulario' })}
                                                textField="Name"
                                                value={detail.Products?.find(product => product.Id === state.productId)}
                                            />
                                            <div className="small text-muted mt-1">
                                                <FormattedMessage defaultMessage="The product this code locked to when the student first launched a test. Change it to move their access to a different product in the bundle." id="the_product_this_code_locked_to_when_the.b4b7650c" />
                                            </div>
                                        </div>
                                    </div>
                                    )}
                                </div>
                            </div>

                            {(state.cloudAccessEnabled || detail.CloudAccessRemaining != null || detail.TestsRemaining !== null || detail.BooksRemaining !== null || detail.CoursesRemaining !== null) && (
                            <div className="card">
                                <div className="card-body d-flex flex-column gap-3">
                                    <h6 className="text-uppercase text-muted small fw-bold m-0">
                                        <FormattedMessage defaultMessage="Content" id="content.e305279d" />
                                    </h6>

                                    {(state.cloudAccessEnabled || detail.CloudAccessRemaining != null) && (
                                    <div className="row">
                                        <div className="col-12">
                                            <GMetrixInput
                                                label={formatMessage({ id: 'cloud_access_available.6a38d888', defaultMessage: 'Cloud Access Available' })}
                                                min={0}
                                                onChange={e => setState({ cloudAccessRemaining: e.target.value.replace(/^0+(?=\d)/, '') })}
                                                readOnlyNoLock={!state.cloudAccessEnabled}
                                                type="number"
                                                value={state.cloudAccessEnabled ? state.cloudAccessRemaining : '0'}
                                            />
                                            {cloudAccessInvalid && (
                                            <div className="text-danger small mt-1">
                                                <FormattedMessage defaultMessage="Cloud access must be a whole number of 0 or more." id="cloud_access_must_be_a_whole_number_of_0.fd132f71" />
                                            </div>
                                            )}
                                        </div>
                                    </div>
                                    )}

                                    <div className="d-flex flex-wrap gap-3">
                                        {detail.TestsRemaining !== null && (
                                        <GMetrixInput
                                            label={formatMessage({ id: 'tests_available.9288f12e', defaultMessage: 'Tests Available' })}
                                            min={0}
                                            onChange={e => setState({ testsRemaining: e.target.value })}
                                            type="number"
                                            value={state.testsRemaining}
                                        />
                                        )}

                                        {detail.BooksRemaining !== null && (
                                        <GMetrixInput
                                            label={formatMessage({ id: 'books_available.1164fb1d', defaultMessage: 'Books Available' })}
                                            min={0}
                                            onChange={e => setState({ booksRemaining: e.target.value })}
                                            type="number"
                                            value={state.booksRemaining}
                                        />
                                        )}

                                        {detail.CoursesRemaining !== null && (
                                        <GMetrixInput
                                            label={formatMessage({ id: 'courses_available.c050e4c4', defaultMessage: 'Courses Available' })}
                                            min={0}
                                            onChange={e => setState({ coursesRemaining: e.target.value })}
                                            type="number"
                                            value={state.coursesRemaining}
                                        />
                                        )}
                                    </div>
                                </div>
                            </div>
                            )}
                        </div>
                    )}
            </Modal.Body>
            <Modal.Footer className="border-0">
                <GMetrixButton
                    disabled={state.saving}
                    onClick={hide}
                    variant="outline-secondary"
                >
                    <FormattedMessage defaultMessage="Cancel" id="cancel.89a86adc" />
                </GMetrixButton>
                <GMetrixButton
                    disabled={saveDisabled}
                    onClick={handleSave}
                    variant="primary"
                >
                    <FormattedMessage defaultMessage="Save" id="save.d44082ae" />
                </GMetrixButton>
            </Modal.Footer>
        </Modal>
    );

});

EditStudentAccessDialog.displayName = 'EditStudentAccessDialog';
